import React, { useState, useEffect } from "react";
import {
  getTimeSpentSeconds,
  formatTimeSpent,
  formatQuestionDuration,
} from "../utils/timeSpent";
import { Form, Row, Col, Card, Table, Spinner } from "react-bootstrap";
import { useLocation } from "react-router-dom";

function AttemptQuestions({ attemptId }) {
  const [questions, setQuestions] = useState([]);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const { assessmentId, userId: viewedUserId } = location.state || {};
  const [summary, setSummary] = useState({
    answered: 0,
    correct: 0,
    totalTime: null,
    totalQuestions: 0,
  });
  const [reviewQuestion, setReviewQuestion] = useState(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    if (viewedUserId) {
      setUserId(viewedUserId);
      return;
    }

    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser?.user_id) {
          setUserId(parsedUser.user_id);
        }
      } catch (error) {
        console.error("Error parsing user from localStorage:", error);
      }
    }
  }, [viewedUserId]);

  useEffect(() => {
    const fetchTotalQuestions = async () => {
      if (!assessmentId) return;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API}/getTotalQuestions?assessment_id=${assessmentId}`
        );
        const data = await response.json();
        // console.log("Total questions response:", data);
        if (data.success) {
          setSummary((prev) => ({
            ...prev,
            totalQuestions: data.totalQuestions,
          }));
        } else {
          console.error("Error fetching total questions:", data.message);
        }
      } catch (error) {
        console.error("Error fetching total questions:", error);
      }
    };

    fetchTotalQuestions();
  }, [assessmentId]);

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!userId || !assessmentId || !attemptId) return;

      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API}/getAttempt?user_id=${userId}&assessment_id=${assessmentId}&attempt=${attemptId}`
        );
        const data = await response.json();
        if (data.success) {
          const formattedQuestions = data.data.map((question) => ({
            ...question,
            options: question.answers.map((answer) => ({
              answer_id: answer.answer_id,
              text: answer.answer_text,
              is_correct: parseInt(answer.is_correct, 10),
              choice_status: answer.choice_status,
              answer_status: answer.answer_status,
              score: answer.score || 0,
            })),
          }));

          const totalAnswered = formattedQuestions.filter((q) =>
            q.options.some((a) => a.choice_status === "Chosen")
          ).length;
          const totalCorrect = formattedQuestions.filter((q) =>
            q.options.some(
              (a) => a.is_correct === 1 && a.choice_status === "Chosen"
            )
          ).length;
          const totalTimeSpent = getTimeSpentSeconds(formattedQuestions);

          setQuestions(formattedQuestions);
          setSummary((prev) => ({
            ...prev,
            answered: totalAnswered,
            correct: totalCorrect,
            totalTime: formatTimeSpent(totalTimeSpent),
          }));
        } else {
          console.error("Error fetching questions:", data.message);
        }
      } catch (error) {
        console.error("Error fetching questions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [userId, assessmentId, attemptId]);

  // Scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 200) {
        setShowScrollButton(true);
      } else {
        setShowScrollButton(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleReviewChange = (e) => {
    const selectedQuestion = parseInt(e.target.value, 10);
    if (!isNaN(selectedQuestion)) {
      setReviewQuestion(selectedQuestion - 1);
    }
  };

  return (
    <div className="container p-3" style={{ fontFamily: "Manrope-Regular" }}>
      {loading ? (
        <div
          className="d-flex flex-column justify-content-center align-items-center"
          style={{ height: "80vh" }}
        >
          <Spinner animation="border" variant="primary" />
          <p className="mt-3" style={{ fontFamily: "Manrope-Regular" }}>
            Please Wait...
          </p>
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <p className="mb-0">
                <span className="me-3">
                  Answered:{" "}
                  <strong>
                    {summary.answered}/{summary.totalQuestions || "0"}
                  </strong>
                </span>
                <span className="me-3">
                  Correct: <strong>{summary.correct}</strong>
                </span>
                {summary.totalTime && (
                  <span>
                    Time spent: <strong>{summary.totalTime}</strong>
                  </span>
                )}
              </p>
            </div>

            <div className="d-flex align-items-center">
              <Form.Check
                type="checkbox"
                id="select-all-questions"
                label="All"
                className="me-2"
                onChange={(e) => {
                  if (e.target.checked) {
                    setReviewQuestion(null);
                  } else {
                    setReviewQuestion(-1);
                  }
                }}
              />
              <select
                className="form-select"
                style={{ width: "150px" }}
                onChange={handleReviewChange}
                value={reviewQuestion !== null ? reviewQuestion + 1 : ""}
              >
                <option value="">Review</option>
                {questions.map((_, index) => (
                  <option key={index} value={index + 1}>
                    Question {index + 1}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {(reviewQuestion !== null
            ? [questions[reviewQuestion]]
            : questions
          ).map((question, questionIndex) =>
            question ? (
              <Card key={questionIndex} className="mb-4">
                <Card.Body>
                  <Row className="align-items-center mb-3">
                    <Col md={8}>
                      <h6 className="fw-semibold">
                        Question{" "}
                        {reviewQuestion !== null
                          ? reviewQuestion + 1
                          : questionIndex + 1}{" "}
                        of {questions.length}
                      </h6>
                    </Col>
                    <Col md={4} className="text-end">
                      <p>
                        <strong>Score:</strong>{" "}
                        {question.options.find(
                          (option) => option.choice_status === "Chosen"
                        )?.score || 0}
                      </p>
                    </Col>
                  </Row>

                  {formatQuestionDuration(question) && (
                    <p>
                      <strong>Time Spent:</strong>{" "}
                      {formatQuestionDuration(question)}
                    </p>
                  )}

                  <h6 className="fw-semibold">{question.question_text}</h6>

                  <Table bordered responsive className="mt-3">
                    <colgroup>
                      <col style={{ width: "60%" }} />
                      <col style={{ width: "20%" }} />
                      <col style={{ width: "20%" }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Selected Answer</th>
                        <th className="text-center">Correct Answer</th>
                        <th className="text-center">Your Choice</th>
                      </tr>
                    </thead>
                    <tbody>
                      {question.options.map((option, index) => (
                        <tr key={index}>
                          <td>
                            <Form.Check
                              type="radio"
                              label={option.text}
                              name={`question-${questionIndex}`}
                              checked={option.choice_status === "Chosen"}
                              readOnly
                            />
                          </td>
                          <td className="text-center">
                            {option.is_correct === 1 && (
                              <span
                                style={{ color: "green", fontWeight: "bold" }}
                              >
                                ✓
                              </span>
                            )}
                          </td>
                          <td className="text-center">
                            {option.choice_status === "Chosen" ? (
                              option.answer_status === "Correct" ? (
                                <span
                                  style={{ color: "green", fontWeight: "bold" }}
                                >
                                  ✓
                                </span>
                              ) : (
                                <span
                                  style={{ color: "red", fontWeight: "bold" }}
                                >
                                  ✗
                                </span>
                              )
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            ) : null
          )}

          {showScrollButton && (
            <button
              onClick={handleScrollToTop}
              style={{
                position: "fixed",
                bottom: "20px",
                right: "20px",
                width: "50px",
                height: "50px",
                backgroundColor: "#1D668F",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                cursor: "pointer",
              }}
              aria-label="Scroll to top"
            >
              ↑
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default AttemptQuestions;
